var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, useRef } from "react";
import { FloatingArrow, FloatingFocusManager, FloatingPortal, arrow, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions, useRole, useTransitionStyles, } from "@floating-ui/react";
import classNames from "classnames";
import { QuiPopoverContext } from "./QuiPopoverContext.js";
import { noop } from "../../utils/index.js";
const ARROW_HEIGHT = 8;
const GAP = 2;
export function QuiPopover(_a) {
    var { children, content, disableDismissOnOutsideClick, disableDismissOnEscapeKeyDown, initialFocus, isOpen, onClose, hideArrow, placement, variant, padding = "lg", className, style } = _a, otherProps = __rest(_a, ["children", "content", "disableDismissOnOutsideClick", "disableDismissOnEscapeKeyDown", "initialFocus", "isOpen", "onClose", "hideArrow", "placement", "variant", "padding", "className", "style"]);
    const arrowRef = useRef(null);
    const offsetValue = (hideArrow ? 0 : ARROW_HEIGHT) + GAP;
    const { refs, context, floatingStyles } = useFloating({
        placement,
        open: isOpen,
        onOpenChange: onClose,
        whileElementsMounted: autoUpdate,
        middleware: [
            flip(),
            shift(),
            offset(offsetValue),
            arrow({
                padding: GAP,
                element: arrowRef,
            }),
        ],
    });
    const { isMounted, styles } = useTransitionStyles(context, {
        duration: 200,
    });
    const dismiss = useDismiss(context, {
        outsidePress: !disableDismissOnOutsideClick,
        escapeKey: disableDismissOnEscapeKeyDown !== true,
    });
    const role = useRole(context, {
        role: "tooltip",
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);
    const cn = classNames(className, "qui-popover", {
        "qui-popover-variant-black": variant === "black",
    }, padding && `qui-padding-${padding.replace(" ", "-")}`);
    const contextValue = useMemo(() => ({
        isOpen,
        close: onClose !== null && onClose !== void 0 ? onClose : noop,
    }), [isOpen, onClose]);
    return (React.createElement(QuiPopoverContext.Provider, { value: contextValue },
        React.createElement("span", Object.assign({ className: "qui-d-inline-flex", ref: refs.setReference }, getReferenceProps()), children),
        React.createElement(FloatingPortal, null, isMounted && (React.createElement(FloatingFocusManager, { context: context, initialFocus: initialFocus === undefined ? refs.floating : initialFocus },
            React.createElement("div", Object.assign({ ref: refs.setFloating, style: Object.assign(Object.assign(Object.assign({}, floatingStyles), styles), style) }, getFloatingProps({
                className: cn,
            }), otherProps),
                content,
                !hideArrow && (React.createElement(FloatingArrow, { height: ARROW_HEIGHT, tipRadius: 2, className: "qui-popover-arrow", ref: arrowRef, context: context, stroke: variant === "black" ? undefined : "var(--arrow-stroke)", strokeWidth: 0.5 }))))))));
}
